import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import {
  createEntry, updateEntry,
  SHIFT_PERIODS, STOOL_TYPES, AMOUNTS, CONTINENCE,
} from './entriesApi';

const NONE_SENTINEL = '__none__';
const fromSelect = (v) => (v === NONE_SENTINEL ? '' : v);
const toSelect = (v) => (v ? v : NONE_SENTINEL);

function nowLocalIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function BowelChartEntryFormModal({
  open, onOpenChange, participantId, chartId, initialEntry = null, onSaved,
}) {
  const isEdit = !!initialEntry;
  const [entryDate, setEntryDate] = useState('');
  const [shiftPeriod, setShiftPeriod] = useState('');
  const [bowelOpened, setBowelOpened] = useState('true'); // radio string
  const [stoolType, setStoolType] = useState('');
  const [amount, setAmount] = useState('');
  const [continence, setContinence] = useState('');
  const [painDiscomfort, setPainDiscomfort] = useState('false');
  const [painNotes, setPainNotes] = useState('');
  const [intervention, setIntervention] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialEntry) {
      // Convert ISO to local datetime-local value
      let d = initialEntry.entry_date || '';
      if (d) {
        try {
          const dt = new Date(d);
          const pad = (n) => String(n).padStart(2, '0');
          d = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        } catch { /* keep raw */ }
      }
      setEntryDate(d);
      setShiftPeriod(initialEntry.shift_period || '');
      setBowelOpened(String(!!initialEntry.bowel_opened));
      setStoolType(initialEntry.stool_type || '');
      setAmount(initialEntry.amount || '');
      setContinence(initialEntry.continence || '');
      setPainDiscomfort(initialEntry.pain_discomfort ? 'true' : 'false');
      setPainNotes(initialEntry.pain_notes || '');
      setIntervention(initialEntry.intervention_taken || '');
      setNotes(initialEntry.notes || '');
    } else {
      setEntryDate(nowLocalIso());
      setShiftPeriod('');
      setBowelOpened('true');
      setStoolType('');
      setAmount('');
      setContinence('');
      setPainDiscomfort('false');
      setPainNotes('');
      setIntervention('');
      setNotes('');
    }
  }, [open, initialEntry]);

  const opened = bowelOpened === 'true';
  const hasPain = painDiscomfort === 'true';

  const handleSave = async () => {
    if (!entryDate) { toast.error('Entry date/time is required'); return; }
    const payload = {
      entry_date: new Date(entryDate).toISOString(),
      shift_period: shiftPeriod || null,
      bowel_opened: opened,
      stool_type: opened ? (stoolType || null) : null,
      amount: opened ? (amount || null) : null,
      continence: opened ? (continence || null) : null,
      pain_discomfort: hasPain,
      pain_notes: hasPain ? (painNotes || '') : '',
      intervention_taken: intervention || '',
      notes: notes || '',
    };
    try {
      setSaving(true);
      const saved = isEdit
        ? await updateEntry(participantId, chartId, initialEntry.id, payload)
        : await createEntry(participantId, chartId, payload);
      toast.success(isEdit ? 'Entry updated' : 'Entry recorded');
      onOpenChange(false);
      onSaved?.(saved);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.message || 'Failed to save entry';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="entry-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit bowel chart entry' : 'Record bowel chart entry'}</DialogTitle>
          <DialogDescription>
            All entries are timestamped and audit-logged. Fields adapt to whether a bowel action was observed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="entry-date" className="text-xs">Date & time *</Label>
              <Input
                id="entry-date"
                type="datetime-local"
                value={entryDate}
                onChange={e => setEntryDate(e.target.value)}
                className="mt-1"
                data-testid="entry-date-input"
              />
            </div>
            <div>
              <Label htmlFor="entry-shift" className="text-xs">Shift period</Label>
              <Select value={toSelect(shiftPeriod)} onValueChange={(v) => setShiftPeriod(fromSelect(v))}>
                <SelectTrigger id="entry-shift" className="mt-1" data-testid="entry-shift-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_SENTINEL}>— None</SelectItem>
                  {SHIFT_PERIODS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs block mb-1.5">Bowel action observed? *</Label>
            <RadioGroup value={bowelOpened} onValueChange={setBowelOpened} className="flex gap-4" data-testid="entry-bowel-opened">
              <div className="flex items-center gap-1.5"><RadioGroupItem value="true" id="bo-yes" data-testid="bo-yes" /><Label htmlFor="bo-yes" className="text-sm cursor-pointer">Yes</Label></div>
              <div className="flex items-center gap-1.5"><RadioGroupItem value="false" id="bo-no" data-testid="bo-no" /><Label htmlFor="bo-no" className="text-sm cursor-pointer">No</Label></div>
            </RadioGroup>
          </div>

          {opened && (
            <div className="grid grid-cols-1 gap-3 p-3 rounded-md bg-muted/40 border" data-testid="entry-clinical-fields">
              <div>
                <Label htmlFor="entry-stool" className="text-xs">Stool type (Bristol scale)</Label>
                <Select value={toSelect(stoolType)} onValueChange={(v) => setStoolType(fromSelect(v))}>
                  <SelectTrigger id="entry-stool" className="mt-1" data-testid="entry-stool-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_SENTINEL}>— Not recorded</SelectItem>
                    {STOOL_TYPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="entry-amount" className="text-xs">Amount</Label>
                  <Select value={toSelect(amount)} onValueChange={(v) => setAmount(fromSelect(v))}>
                    <SelectTrigger id="entry-amount" className="mt-1" data-testid="entry-amount-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_SENTINEL}>— Not recorded</SelectItem>
                      {AMOUNTS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="entry-continence" className="text-xs">Continence</Label>
                  <Select value={toSelect(continence)} onValueChange={(v) => setContinence(fromSelect(v))}>
                    <SelectTrigger id="entry-continence" className="mt-1" data-testid="entry-continence-select"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_SENTINEL}>— Not recorded</SelectItem>
                      {CONTINENCE.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs block mb-1.5">Pain / discomfort?</Label>
            <RadioGroup value={painDiscomfort} onValueChange={setPainDiscomfort} className="flex gap-4" data-testid="entry-pain">
              <div className="flex items-center gap-1.5"><RadioGroupItem value="false" id="pd-no" /><Label htmlFor="pd-no" className="text-sm cursor-pointer">No</Label></div>
              <div className="flex items-center gap-1.5"><RadioGroupItem value="true" id="pd-yes" /><Label htmlFor="pd-yes" className="text-sm cursor-pointer">Yes</Label></div>
            </RadioGroup>
            {hasPain && (
              <Textarea
                value={painNotes}
                onChange={e => setPainNotes(e.target.value)}
                placeholder="Describe the pain/discomfort (up to 500 chars)"
                maxLength={500}
                rows={2}
                className="mt-2"
                data-testid="entry-pain-notes"
              />
            )}
          </div>

          <div>
            <Label htmlFor="entry-intervention" className="text-xs">Intervention taken</Label>
            <Textarea
              id="entry-intervention"
              value={intervention}
              onChange={e => setIntervention(e.target.value)}
              placeholder="e.g. pear juice, prune, medication as charted"
              maxLength={1000}
              rows={2}
              className="mt-1"
              data-testid="entry-intervention-input"
            />
          </div>
          <div>
            <Label htmlFor="entry-notes" className="text-xs">Additional notes</Label>
            <Textarea
              id="entry-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Shift-appropriate context for the next staff member"
              maxLength={2000}
              rows={3}
              className="mt-1"
              data-testid="entry-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="entry-form-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="entry-form-submit">
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Record entry')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
